import { Avatar, type AvatarSubject } from '@/components/Avatar'
import {
  Section,
  SectionContent,
  SectionHeader,
  SectionTitleSuffix,
  useSection,
} from '@/components/Section'
import { formatSentDate } from '@/features/admin/scope-change-requests/scope-change-request-formatters'
import styles from './ResponsesSection.module.css'

export interface ResponsesSectionUser extends AvatarSubject {
  displayName: string
}

export interface ResponsesSectionReason {
  createdBy: ResponsesSectionUser
  collaboratorType: string
  reason: string
  createTime: Date
}

export interface ResponsesSectionResponse {
  createdByUser: ResponsesSectionUser
  responderUser: string
  reason: string | null
  displayResponseType: string
  dateCreated: Date
}

interface ChangeRequestReasonItemProps {
  reason: ResponsesSectionReason
}

function ChangeRequestReasonItem({ reason }: ChangeRequestReasonItemProps) {
  return (
    <div className={styles.responseRoot}>
      <div className={styles.responseUser}>
        <Avatar subject={reason.createdBy} size={24} />
        <div className={styles.responseUserName}>
          {reason.createdBy.displayName}
        </div>

        <div className={styles.separator}>•</div>
        <div className={styles.responseUserType}>{reason.collaboratorType}</div>
      </div>
      <div className={styles.response}>{reason.reason}</div>
      <div className={styles.responseDate}>
        {'Created'} {formatSentDate(reason.createTime)}
      </div>
    </div>
  )
}

interface ChangeRequestResponseItemProps {
  response: ResponsesSectionResponse
}

function ChangeRequestResponseItem({
  response,
}: ChangeRequestResponseItemProps) {
  return (
    <div className={styles.responseRoot}>
      <div className={styles.responseUser}>
        <Avatar subject={response.createdByUser} size={24} />
        <div className={styles.responseUserName}>
          {response.createdByUser.displayName}
        </div>

        <div className={styles.separator}>•</div>
        <div className={styles.responseUserType}>{response.responderUser}</div>
      </div>
      <div className={styles.response}>{response.reason}</div>
      <div className={styles.responseDate}>
        {response.displayResponseType} {formatSentDate(response.dateCreated)}
      </div>
    </div>
  )
}

export interface ResponsesSectionProps {
  responses: ResponsesSectionResponse[]
  requestReason: ResponsesSectionReason
}

export function ResponsesSection({
  responses,
  requestReason,
}: ResponsesSectionProps) {
  const sectionProps = useSection(false)

  return (
    <Section {...sectionProps}>
      <SectionHeader>
        Responses{' '}
        <SectionTitleSuffix>({responses.length + 1})</SectionTitleSuffix>
      </SectionHeader>
      <SectionContent>
        {responses.length > 0 &&
          responses.map((r) => (
            <ChangeRequestResponseItem
              key={`${r.dateCreated.getTime()}-${r.responderUser}`}
              response={r}
            />
          ))}
        <ChangeRequestReasonItem reason={requestReason} />
      </SectionContent>
    </Section>
  )
}
